import { MarketProbability } from "@tracker-bet-analystic/domain";
import { formatDecimal } from "@tracker-bet-analystic/domain";

interface MarketProbabilityCardProps {
  market: MarketProbability;
}

const statusLabels: Record<MarketProbability["status"], { label: string; className: string }> = {
  experimental_value: { label: "Vantagem detectada", className: "badge-success" },
  no_value: { label: "Sem vantagem", className: "badge-danger" },
  awaiting_odds: { label: "Aguardando odds", className: "badge-warning" },
  insufficient_data: { label: "Dados insuficientes", className: "badge-neutral" },
  unsupported_market: { label: "Mercado não suportado", className: "badge-neutral" },
};

const familyLabels: Record<string, string> = {
  result_1x2: "Resultado (1X2)",
  total_goals: "Total de Gols",
  both_teams_to_score: "Ambas Marcam",
  handicap: "Handicap",
  total_corners: "Escanteios",
  total_cards: "Cartões",
  total_shots: "Finalizações",
  total_shots_on_target: "Finalizações no Alvo",
};

const selectionLabels: Record<string, string> = {
  home: "Mandante",
  away: "Visitante",
  draw: "Empate",
  over: "Over",
  under: "Under",
  yes: "Sim",
  no: "Não",
};

export function MarketProbabilityCard({ market }: MarketProbabilityCardProps) {
  const { status, marketKey, pWin, pPush, pLoss, fairOdds, ev, reason } = market;
  const { label, className } = statusLabels[status];
  const familyLabel = familyLabels[marketKey.family] ?? marketKey.family;
  const selectionLabel = selectionLabels[marketKey.selection] ?? marketKey.selection;

  const marketLabel = `${familyLabel} ${marketKey.selection === "over" || marketKey.selection === "under" ? `${selectionLabel} ${marketKey.line}` : selectionLabel}`;

  return (
    <article className="card p-4 border-l-4 border-l-[var(--color-primary)]">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-semibold text-[var(--color-text)] truncate">{marketLabel}</h3>
        <span className={className} aria-label={label}>{label}</span>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">Chance de vitória</span>
          <span className="font-mono font-medium">{pWin !== null ? (pWin * 100).toFixed(1) + "%" : "—"}</span>
        </div>
        {pPush !== null && pPush > 0 && (
          <div className="flex justify-between">
            <span className="text-[var(--color-text-muted)]">Chance de empate ou devolução</span>
            <span className="font-mono font-medium">{(pPush * 100).toFixed(1)}%</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-[var(--color-text-muted)]">Chance de derrota</span>
          <span className="font-mono font-medium">{pLoss !== null ? (pLoss * 100).toFixed(1) + "%" : "—"}</span>
        </div>

        {fairOdds && (
          <div className="flex justify-between pt-2 border-t border-[var(--color-border)]">
            <span className="text-[var(--color-text-muted)]">Odd justa</span>
            <span className="font-mono font-medium text-[var(--color-primary)]">{formatDecimal(fairOdds)}</span>
          </div>
        )}

        {ev !== null && (
          <div className="flex justify-between pt-2 border-t border-[var(--color-border)]">
            <span className="text-[var(--color-text-muted)]">Vantagem estimada</span>
            <span className={`font-mono font-medium ${ev > 0 ? "text-[var(--color-success)]" : "text-[var(--color-danger)]"}`}>
              {(ev * 100).toFixed(1)}%
            </span>
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-[var(--color-text-muted)]">{reason}</p>
    </article>
  );
}
